/**
 * Labs — the admin-only shelf at the bottom of Settings.
 *
 * `isAdminUser` is cosmetic: it decides whether the section is drawn at all.
 * The real boundary is server-side, where the endpoint reads the address off
 * the decoded Firebase token rather than off the request body.
 *
 * The Skylight wordmark sits on a destination chip above the button so the
 * place the week lands is named rather than guessed. The chip is a tonal
 * surface shift, not a rule — DESIGN.md forbids 1px separators.
 */

import React from 'react';
import { View, Text, Image, StyleSheet } from 'react-native';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { RootState } from '../../redux/store';
import { isAdminUser } from '../../utils/isAdmin';
import {
  Paprika,
  PaprikaRadius,
  PaprikaShadows,
  PaprikaSpacing,
  PaprikaTypography,
} from '../../styles/theme';
import { SkylightPushButton } from './SkylightPushButton';

const skylightLogo = require('../../../assets/images/skylight-logo.png');

export const LabsSection: React.FC = () => {
  const { t } = useTranslation();
  const email = useSelector((state: RootState) => state.auth?.user?.email ?? null);

  if (!isAdminUser(email)) return null;

  return (
    <View style={styles.section} testID="labs-section">
      <Text style={styles.sectionTitle}>{t('settings.labs.title')}</Text>
      <Text style={styles.description}>{t('settings.labs.description')}</Text>

      <View style={styles.destination}>
        <Image source={skylightLogo} style={styles.logo} resizeMode="contain" />
        <Text style={styles.destinationText}>{t('settings.labs.skylightDestination')}</Text>
      </View>

      <SkylightPushButton />
    </View>
  );
};

const styles = StyleSheet.create({
  // Same card as every other section in settings.tsx.
  section: {
    width: '100%',
    marginBottom: PaprikaSpacing.md,
    padding: PaprikaSpacing.lg,
    backgroundColor: Paprika.cardBg,
    borderRadius: PaprikaRadius.lg,
    borderWidth: 1,
    borderColor: Paprika.border,
    ...PaprikaShadows.card,
  },
  sectionTitle: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
    marginBottom: PaprikaSpacing.md,
  },
  description: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
    marginBottom: PaprikaSpacing.md,
  },
  destination: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Paprika.surfaceLow,
    borderRadius: PaprikaRadius.md,
    paddingVertical: PaprikaSpacing.sm,
    paddingHorizontal: PaprikaSpacing.md,
  },
  logo: {
    height: 15,
    // 170x51 artwork. `aspectRatio` alone is not enough on web: react-native-web
    // stamps the asset's intrinsic width (170px) onto the element as an inline
    // style, and a box with a definite width AND height ignores its aspect
    // ratio — the wordmark then floats in the middle of a 170px box and shoves
    // the chip's label 120px to the right. The width is stated explicitly so
    // the box is the size of the artwork on every platform; the ratio is kept
    // alongside it so the two can never silently disagree.
    width: 15 * (170 / 51),
    aspectRatio: 170 / 51,
    marginRight: PaprikaSpacing.sm,
  },
  destinationText: {
    ...PaprikaTypography.caption,
    color: Paprika.textSecondary,
  },
});

export default LabsSection;
